import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpMethods, HttpStrategies } from '@cbre/http';
import { DataObject } from '@shared/models/data-object.model';
import { simpleValueValidation } from '@miq/utils';
import { PaginatedList } from '@shared/models/paginated-list.model';
import { baseTabMap } from '@records/records-search/records-search.constants';
import { SearchResults } from '@records/records-search/shared/search-results.model';
import { TicketMoveEvent } from '@shared/components/service-tickets/ticket-list/ticket-move-event.model';
import { AssignTicket, HelpTicket, TicketFilter, TicketStatus } from '@shared/models/help-ticket.model';
import { ICatalog, IOptionDropdown, ITimeTrack, PlainOption } from '@shared/models/icommons.model';
import { IUser } from '@shared/models/iuser.model';
import { MapCardView } from '@shared/models/map-card-view.model';
import { ReportPayload } from '@shared/models/report.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, share, take } from 'rxjs/operators';
import { LookupRefService } from './lookup-ref.service';
import { EntityType } from '@shared/models/entity-enum';
import { lowerNoSpaces } from '@shared/functions';
import { getSearchFilterRequestByUserGroupAndEntity } from './models/search-filters.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

const HELP_TICKETS = '/help/tickets';

interface CBREEmployee {
  work_email: string;
  first_name: string;
  last_name: string;
}

const DEFAULT_TARGET_CATALOG = 'Records Page';

export function transformCbreEmployeesToIUsers(employees: CBREEmployee[]): IUser[] {
  return employees.map(cbreEmployee => {
    const { work_email, first_name, last_name } = cbreEmployee;

    const emailAddress = (work_email || '').trim();
    const firstName = (first_name || '').trim();
    const lastName = (last_name || '').trim();

    return { emailAddress, firstName, lastName, exists: false };
  });
}

@Injectable({
  providedIn: 'root',
})
export class SharedService extends HttpMethods {
  private getEntitiesStream$: Observable<IOptionDropdown[]>;
  constructor(
    http: HttpClient,
    private lookupService: LookupRefService,
    public userInfoResolver: UserInfoResolver,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    super(http);
  }

  public getAppCatalog(): Observable<ICatalog[]> {
    return this.lookupService.loadDataFor('/lookup/appcatalog');
  }

  public findAppCatalogItem(targetCatalog: string): Observable<ICatalog> {
    return this.getAppCatalog().pipe(
      map(catalog => (!!catalog ? catalog : [])),
      map(catalogs =>
        catalogs.find(
          catalogItem =>
            lowerNoSpaces(catalogItem.value.toLowerCase()) ===
            lowerNoSpaces(targetCatalog.toLowerCase() || DEFAULT_TARGET_CATALOG)
        )
      )
    );
  }

  public getServiceTicketStatuses(): Observable<ICatalog[]> {
    const uri = '/lookup/statuses/serviceticket';
    return this.httpGet(uri);
  }

  /**
   *  Request to get QTD by market id and report id
   */

  public getMarketQtd(marketId: number, reportId: number): Observable<number> {
    const uri = `/reports/${reportId}/qtd?market=${marketId}`;
    return this.httpGet(uri);
  }

  public startTracking(trackDetails: ITimeTrack): Observable<number> {
    const uri = '/usage/start';
    return this.httpPost(uri, trackDetails);
  }

  public confirmActivity(recordId) {
    const uri = `/usage/confirm/${recordId}`;
    return this.httpPut(uri);
  }

  public getDefaultSettings() {
    const uri = '/users/clone';
    const params = new HttpParams({
      fromObject: {
        email: 'default.user@cbre.com',
        type: 'settings',
      },
    });
    return this.httpGet(uri, params);
  }

  public getTickets(
    offset: number,
    limit: number,
    sortField: string,
    sortOrder: number,
    userId?: number
  ): Observable<PaginatedList<HelpTicket[]>> {
    const uri = HELP_TICKETS;
    const options: {
      offset: string;
      limit: string;
      sortField: string;
      sortOrder: string;
      userId?: string;
    } = {
      offset: offset.toString(),
      limit: limit.toString(),
      sortField,
      sortOrder: sortOrder > 0 ? 'ASC' : 'DESC',
    };

    if (!!userId) {
      options.userId = userId.toString();
    }

    const params = new HttpParams({ fromObject: options });
    return this.httpGet(uri, params);
  }

  public serviceTicketsFiltering(data: TicketFilter, options = {}): Observable<PaginatedList<HelpTicket[]>> {
    const uri = HELP_TICKETS;
    return this.httpPost(uri, data, { params: new HttpParams({ fromObject: options }) });
  }

  public exportTicketsAsCsv(data: TicketFilter, options: Record<string, string> = {}): Observable<Blob> {
    const uri = `${HELP_TICKETS}/export`;
    return this.httpPost(uri, data, {
      params: new HttpParams({ fromObject: options }),
      responseType: 'blob',
    });
  }

  public putTicketToTop(ticketsIds: number[]): Observable<HelpTicket[]> {
    const uri = `${HELP_TICKETS}/top`;
    return this.httpPut(uri, ticketsIds);
  }

  public putTicketToBottom(ticketsIds: number[]): Observable<HelpTicket[]> {
    const uri = `${HELP_TICKETS}/bottom`;
    return this.httpPut(uri, ticketsIds);
  }

  public putMoveTicket(ticketId: number, moveTicketEvent: TicketMoveEvent): Observable<PaginatedList<HelpTicket>> {
    const uri = `${HELP_TICKETS}/${ticketId}/move`;
    return this.httpPut(uri, moveTicketEvent);
  }

  public postGroupTickets(tickets: HelpTicket[][]) {
    const uri = `${HELP_TICKETS}/group`;
    return this.httpPost(uri, tickets);
  }

  public postUngroupTickets(tickets: HelpTicket[][]) {
    const uri = `${HELP_TICKETS}/ungroup`;
    return this.httpPost(uri, tickets);
  }

  public deleteTicket(ticketId: number): Observable<any> {
    const uri = `${HELP_TICKETS}/${ticketId}`;
    return this.httpDelete(uri);
  }

  async stopTracking(recordId) {
    if (recordId == null) {
      return;
    }
    const uri = `/usage/stop/${recordId}`;
    await this.httpPut(uri).toPromise();
  }

  public getAdminUsers(): Observable<IUser[]> {
    const uri = `/users/admins`;
    return this.httpGet(uri);
  }

  public getTicketDetails(ticketId: number): Observable<HelpTicket> {
    const uri = `${HELP_TICKETS}/${ticketId}`;
    return this.httpGet(uri);
  }

  public putTicketDetail(ticketDetail: HelpTicket) {
    const uri = `${HELP_TICKETS}/${ticketDetail.id}`;
    return this.httpPut(uri, ticketDetail);
  }

  public assignTickets(assignTo: AssignTicket) {
    const uri = `${HELP_TICKETS}/assign`;
    return this.httpPut(uri, assignTo);
  }

  public changeTicketStatus(ticketinfo: TicketStatus) {
    const uri = `${HELP_TICKETS}/status`;
    return this.httpPut(uri, ticketinfo);
  }

  public getTicketFilterUsers(params: any): Observable<PlainOption[]> {
    const uri = `${HELP_TICKETS}/lookup`;
    return this.httpGet(uri, params);
  }

  public getEntities(userId: number): Observable<IOptionDropdown[]> {
    if (this.getEntitiesStream$ == null) {
      this.getEntitiesStream$ = this.httpGet(`/clipboard/${userId}`).pipe(share());
    }
    return this.getEntitiesStream$;
  }

  public deleteEntityRecords(userId: number, entity: string, isSpacesViewEnabledFlag = false) {
    return this.httpDelete(`${isSpacesViewEnabledFlag ? '/v2' : ''}/clipboard/${userId}/${entity}`);
  }

  public clearClipboard(userId: number) {
    return this.httpDelete(`/clipboard/${userId}`);
  }

  /**
   * Prepares an array of EDP update requests to then make the calls.
   * At the end, it saves the failed record ids on failedRecords object.
   * It may trigger the top notification so the user can retry the update of
   * the failed ones.
   *
   * @param entity Entity to update.
   * @param updateObject Object which contains more objects with values to update.
   *
   * @deprecated
   */
  public updateOnEDP(entity: string, updateObject) {
    const updateEdpCallObservables = Object.keys(updateObject).map(recordId =>
      this.updateRecordOnEDP(entity, recordId, updateObject[recordId]).pipe(
        map(({ id }) => ({ succeeded: id })),
        catchError(obj => of({ failed: obj['id'] }))
      )
    );

    return forkJoin(updateEdpCallObservables).pipe(
      take(1),
      map(responseArray => responseArray.filter(res => !!res['failed']).map(res => res['failed']))
    );
  }

  public updateRecordOnEDP<T extends { id: number } = { id: number }>(
    entity: string,
    recordId: string,
    updateObject: { [updatePath: string]: any },
    method?: string,
    requestMethod?: HttpStrategies,
    isLeaseSpaceView = false
  ): Observable<T> {
    let paramObj: { entity: string; id: string; method?: string } = { entity, id: recordId };
    if (method != null) {
      paramObj = { ...paramObj, method };
    }
    const params = new HttpParams({ fromObject: paramObj });

    if (isLeaseSpaceView) {
      return this.httpPut(`/write/space`, updateObject);
    }

    // Necessary for certain update payloads that require POST method
    return !!requestMethod && requestMethod === HttpStrategies.POST
      ? this.httpPost(`/write`, updateObject, { params })
      : this.httpPut(`/write`, updateObject, { params });
  }

  public updateRecordOnEDPWithUrl(
    updateUrl: string,
    updateObject: { [updatePath: string]: any },
    requestMethod: HttpStrategies,
    paramObj: Record<string, any>
  ) {
    const params = new HttpParams({ fromObject: paramObj });

    if (requestMethod === HttpStrategies.POST) {
      return this.httpPost(updateUrl, updateObject, { params });
    }
    return this.httpPut(updateUrl, updateObject, { params });
  }

  public createRecordOnEDP(
    entity: string,
    record: DataObject,
    { cloneSourceId }: { cloneSourceId?: number } = {}
  ): Observable<{ id: number }> {
    let sourceParam = '&source=New';
    if (!!cloneSourceId) {
      sourceParam = `&source=Clone&sourceId=${cloneSourceId}`;
    }
    return this.httpPost(`/write?entity=${entity}${sourceParam}`, record);
  }

  public bulkUpdate(
    payloads: DataObject[],
    localizedEntity: string,
    entity: EntityType,
    transactionSourceSuffix: string = null
  ) {
    return this.httpPut(`/bulkmsg/pushAsync`, { requests: payloads, localizedEntity, entity, transactionSourceSuffix });
  }

  public searchRecords(request, limit = 5, type = 'typeAhead'): Observable<any> {
    const { entity, filters, query } = request;
    const tabData = baseTabMap.find(tab => tab.tabName === entity);
    const { textSearchFields = [] } = tabData;

    let requestFilters = filters;
    const typeAheadValue = (query && query.trim()) || '';
    const searchFilter = {
      type: 'multifield',
      field: textSearchFields,
      values: typeAheadValue,
      dataType: 'text',
      typeAhead: true,
    };
    requestFilters =
      typeAheadValue.length === 0
        ? filters.filter(filterItem => filterItem.type !== 'multifield')
        : [...filters.filter(f => f.type !== 'multifield'), searchFilter];
    return this.httpPost(`/search?view=${type}`, { ...request, limit, filters: requestFilters });
  }

  public searchByQuery(request, paramObject = {}) {
    const { entity, query } = request;
    if (query == null) {
      return of([]);
    }
    const params = new HttpParams({ fromObject: { ...paramObject, query: query.trim() } });
    return this.httpGet(`/search/${entity}`, params);
  }

  public searchReonomyPropertyRecords({ userId, filters }, searchType = 'typeAhead'): Observable<any> {
    const request = {
      entity: 'property',
      userId,
      geography: {
        markets: [],
        propertyTypes: [],
        submarkets: [],
        districts: [],
        neighborhoods: [],
      },
      filters,
      offset: 0,
      limit: 10,
    };
    return this.httpPost(`/reonomy?view=${searchType}`, request);
  }

  /**
   * Fetch individual record from reonomy interface
   * @param id Reonomy key
   */
  public fetchReonomyProperty({ userId, reonomyKey }) {
    const request = {
      id: reonomyKey,
      userId,
      entity: 'property',
      view: 'card',
    };
    // /reonomy/record
    return this.httpPost('/reonomy/record', request);
  }

  public searchMiqUser(query: string, status = 'active'): Observable<IUser[]> {
    const params = new HttpParams({ fromObject: { status, query } });
    return this.httpGet(`/users`, params).pipe(map(users => (!!users ? users : [])));
  }

  public searchCBREUser(query: string): Observable<IUser[]> {
    const params = new HttpParams({ fromObject: { query } });
    return this.httpGet(`/search/employees`, params).pipe(
      map(({ results }: SearchResults) => {
        const employees = results || [];
        const employeesWithEmail = employees.filter((employee: CBREEmployee) =>
          simpleValueValidation(employee.work_email)
        );
        return transformCbreEmployeesToIUsers(employeesWithEmail);
      })
    );
  }

  public getExportLog(userId: number, entity: string) {
    return this.httpGet(`/exports/${userId}/${entity}`);
  }

  public createReport(reportInfo: ReportPayload) {
    return this.httpPost(`/reports`, reportInfo);
  }

  public downloadStringAsCSV(_csvContent: string, _filename: string) {
    // There is not reference to this method in the codebase
    // Keeping the method to avoid build errors in case of external usage
    this.logger.warn('downloadStringAsCSV is deprecated in SharedService.');
  }

  searchMapCardView({ userId, ...request }: MapCardView): Observable<SearchResults> {
    userId = userId || this.userInfoResolver.resolve().id;
    const defaultRequest = this.getDefaultFilters(request.entity as EntityType);
    return this.httpPost('/search/record', {
      ...defaultRequest,
      ...request,
      userId,
      view: request.view ? request.view : 'card',
    }).pipe(map((r: SearchResults) => (!!r.results ? r : { ...r, results: [] })));
  }

  getDefaultFilters(entity: EntityType) {
    return getSearchFilterRequestByUserGroupAndEntity(entity) || {};
  }

  searchAliasRecords(userId: number, searchText?: string | number): Observable<SearchResults> {
    const viewValue = 'associate';
    const ignoreHistoryValue = true;
    const request = this.createAliasSearchRequest(userId, searchText);
    return this.httpPost(`/search?view=${viewValue}&ignoreHistory=${ignoreHistoryValue}`, request);
  }

  private createAliasSearchRequest(userId: number, searchText?: string | number) {
    const baseFilters = [];

    if (searchText != null && Number.isNaN(Number(searchText))) {
      baseFilters.push({
        type: 'equalnocase',
        field: 'company_name',
        key: 1300,
        dataType: 'text',
        name: 'Name',
        values: searchText,
      });
    }
    if (searchText != null && !Number.isNaN(Number(searchText))) {
      baseFilters.push(
        {
          type: 'equal',
          field: 'entity.id',
          key: 1358,
          dataType: 'id',
          values: searchText,
        },
        {
          type: 'equalnocase',
          field: 'company_name',
          key: 1300,
          dataType: 'text',
          name: 'Name',
          values: searchText.toString(),
        }
      );
    }
    return {
      skipDefaultFilters: true,
      entity: 'company',
      offset: 0,
      limit: 10,
      filters: [
        {
          values: baseFilters,
          should: true,
          type: 'group',
          field: 'alias_company.company_name',
          key: 9999,
          dataType: 'text',
          group_key: '_1300',
        },
        {
          type: 'multi',
          field: 'hierarchy_type_desc',
          key: 18712,
          values: ['ALIAS'],
          dataType: 'text',
          name: 'Company Hierarchy',
        },
      ],
      userId: userId || this.userInfoResolver?.resolve()?.id,
      navigationSource: 'default',
      searchEngine: null,
      spacesQuery: null,
    };
  }
}
